import { useId, type ReactElement } from 'react';
import { Button } from '@studio/design-system/button';
import * as Dialog from '@studio/design-system/dialog';
import type { BillingRegistryTrialNotice } from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { useTrialNoticeShown } from './use-trial-notice-shown.js';
import type { Room } from '@studio/models/room';

const focusRing = 'focus-visible:ring-2 focus-visible:ring-primary/50';

// Format the confirmed deadline in the device locale without storing timezone data.
export function trialWelcomeDeadline(unix: number, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(new Date(unix * 1000));
}
// Content consumes confirmed structured data; the route trigger and enrollment remain independent.
export function TrialWelcome({
  roomId,
  notice,
  onDismiss,
  onShown,
  onPlanDetails,
  onReturnFocus
}: {
  readonly roomId: Room['id'];
  readonly notice: BillingRegistryTrialNotice;
  readonly onDismiss: () => void;
  readonly onShown?: (() => void) | undefined;
  readonly onPlanDetails?: (() => void) | undefined;
  readonly onReturnFocus: () => void;
}): ReactElement {
  const descriptionId = useId();
  const { ref, reportVisible } = useTrialNoticeShown(onShown);
  const deadline = trialWelcomeDeadline(notice.trialEnd);
  const future = notice.futurePurchase;
  const nextName = notice.nextPlan?.title;
  const nextSentence =
    future !== undefined
      ? `${nextName} is scheduled after the trial. Paid access starts after payment is confirmed.`
      : `After the trial, this Studio moves to ${nextName}.`;
  const dismiss = () => {
    reportVisible();
    onDismiss();
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          ref={ref}
          data-trial-welcome
          closeOnEscape
          aria-describedby={descriptionId}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onReturnFocus();
          }}
          className='fixed left-1/2 top-1/2 flex max-h-[calc(100dvh_-_32px)] w-[calc(100vw_-_32px)] max-w-[480px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 border border-base-50 p-24 shadow-lg'
          {...DataSTag(Sel.TrialWelcome, 'dialog', roomId)}
        >
          <div className='flex shrink-0 items-start gap-12'>
            <Dialog.Title className='min-w-0 grow break-words text-lg'>
              Your free trial is active
            </Dialog.Title>
            <Dialog.Dismiss
              aria-label='Close'
              className={`shrink-0 ${focusRing}`}
              {...STagNew(Sel.TrialWelcome, 'close', roomId)}
            />
          </div>
          <div
            id={descriptionId}
            tabIndex={0}
            className={`leading-relaxed mt-16 min-h-0 overflow-y-auto break-words text-sm text-muted ${focusRing}`}
          >
            <p>
              {notice.studioName} has access to {notice.trialPlan?.title} until {deadline}.
            </p>
            <p className='mt-12'>{nextSentence}</p>
          </div>
          <div className='mt-24 flex shrink-0 flex-wrap gap-8'>
            <Button
              variant='primary'
              onClick={dismiss}
              className={focusRing}
              {...STagNew(Sel.TrialWelcome, 'got-it', roomId)}
            >
              Got it
            </Button>
            {onPlanDetails !== undefined && (
              <Button
                variant='tertiary'
                className={focusRing}
                onClick={() => {
                  reportVisible();
                  onPlanDetails();
                }}
                {...STagNew(Sel.TrialWelcome, 'plan-details', roomId)}
              >
                Plan details
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
